import { jsx } from 'react/jsx-runtime';
import { reactToHTML } from '@fabric-app/ui/utils/reactToHTML';
import { ButtonIcon } from '@fabric-app/ui/components/Button';
import { Node, wrappingInputRule, mergeAttributes } from '@tiptap/core';
import { TextSelection } from '@tiptap/pm/state';
import { IconTextEditorInEditorToggleList } from '@fabric-app/icons/svg';
import { Fragment, DOMParser } from '@tiptap/pm/model';

const inputRegex = /^\s*>\s$/;
const ChevronButton = reactToHTML(/* @__PURE__ */ jsx(ButtonIcon, {
  size: "xss",
  tabIndex: -1,
  className: "fbc-list-chevron",
  children: /* @__PURE__ */ jsx(IconTextEditorInEditorToggleList, {})
}));
const FbcToggleList = Node.create({
  priority: 100,
  name: "fbcToggleList",
  content: "(paragraph | heading) block*",
  group: "block list",
  addAttributes() {
    return {
      expanded: {
        default: true,
        parseHTML: (element) => element.hasAttribute("data-expanded") ? element.getAttribute("data-expanded") === "false" ? false : true : true,
        renderHTML: (attributes) => ({
          "data-expanded": attributes["expanded"]
        }),
        keepOnSplit: true
      }
    };
  },
  parseHTML() {
    return [
      {
        tag: 'div[data-type="fbc-toggle-list"]'
      },
      {
        tag: "details",
        getContent: (element, schema) => {
          if (!(element instanceof HTMLElement)) return Fragment.empty;
          const parser = DOMParser.fromSchema(schema);
          const docFragment = new DocumentFragment();
          docFragment.appendChild(element.cloneNode(true));
          const clonedElement = docFragment.firstChild;
          if (!clonedElement) return Fragment.empty;
          const summary = docFragment.querySelector("summary");
          const summaryTextContent = summary?.textContent;
          const summaryParagraphOrHeading = summary?.querySelector("p, h1, h2, h3, h4, h5, h6");
          const title = summaryParagraphOrHeading ? Fragment.from(parser.parse(summaryParagraphOrHeading)) : summaryTextContent ? Fragment.from(schema.nodes.paragraph.create(null, schema.text(summaryTextContent ?? ""))) : Fragment.empty;
          summary?.remove();
          const parsedContent = parser.parse(clonedElement, {
            topNode: schema.nodes.fbcToggleList.create()
          });
          const fragment = title.append(parsedContent.content);
          return fragment;
        }
      }
    ];
  },
  renderHTML({ HTMLAttributes }) {
    return [
      "div",
      mergeAttributes(HTMLAttributes, {
        "data-type": "fbc-toggle-list"
      }),
      0
    ];
  },
  addNodeView() {
    return ({ editor, node: _node, getPos }) => {
      let node = _node;
      const { dom, contentDOM, updateRender, emptyContent, prefixContent } = renderFbcToggleList(node);
      const onClickPrefixContent = (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        if (!editor.isEditable) return;
        const { view } = editor;
        const tr = view.state.tr;
        const expanded = typeof node.attrs.expanded === "string" ? node.attrs.expanded === "false" ? false : true : !!node.attrs.expanded;
        tr.setNodeMarkup(getPos(), node.type, {
          expanded: !expanded
        });
        view.dispatch(tr);
      };
      const onClickEmptyContent = (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        if (!editor.isEditable) return;
        const { view } = editor;
        const tr = view.state.tr;
        const paragraph = editor.schema.nodes.paragraph.create();
        tr.insert(getPos() + node.nodeSize - 1, paragraph);
        tr.setSelection(TextSelection.create(tr.doc, getPos() + node.nodeSize - 1));
        view.dispatch(tr);
        view.focus();
      };
      prefixContent.addEventListener("click", onClickPrefixContent);
      emptyContent.addEventListener("click", onClickEmptyContent);
      return {
        dom,
        contentDOM,
        destroy: () => {
          prefixContent.removeEventListener("click", onClickPrefixContent);
          emptyContent.removeEventListener("click", onClickEmptyContent);
        },
        update: (updatedNode) => {
          if (updatedNode.type !== node.type) {
            return false;
          }
          node = updatedNode;
          updateRender(node);
          return true;
        }
      };
    };
  },
  addInputRules() {
    const inputRule = wrappingInputRule({
      find: inputRegex,
      type: this.type,
      keepMarks: true
    });
    return [
      inputRule
    ];
  }
});
const renderFbcToggleList = (node) => {
  const dom = document.createElement("div");
  dom.classList.add("fbc-list");
  dom.dataset.type = "fbc-toggle-list";
  dom.dataset.uuid = node.attrs["data-uuid"];
  dom.dataset.createdAt = node.attrs["data-created-at"];
  const main = document.createElement("div");
  main.classList.add("fbc-list-main");
  const prefix = document.createElement("div");
  prefix.classList.add("fbc-list-prefix");
  prefix.contentEditable = "false";
  prefix.style.height = "1.5em";
  const prefixContent = document.createElement("div");
  prefixContent.classList.add("fbc-list-prefix-content");
  prefixContent.innerHTML = ChevronButton;
  prefix.appendChild(prefixContent);
  const content = document.createElement("div");
  content.classList.add("fbc-list-content");
  const emptyContent = document.createElement("div");
  emptyContent.classList.add("fbc-list-empty-content");
  emptyContent.contentEditable = "false";
  emptyContent.tabIndex = 0;
  main.append(prefix, content);
  dom.append(main);
  const updateRender = (node2) => {
    prefixContent.dataset.expanded = node2.attrs.expanded;
    prefixContent.dataset.emptyContent = String(node2.content.childCount === 1);
    dom.dataset.expanded = node2.attrs.expanded;
    dom.dataset.emptyContent = String(node2.content.childCount === 1);
    emptyContent.dataset.dropping = "false";
    if (node2.content.childCount === 1) {
      dom.appendChild(emptyContent);
    } else if (dom.contains(emptyContent)) {
      dom.removeChild(emptyContent);
    }
  };
  updateRender(node);
  return {
    dom,
    contentDOM: content,
    prefixContent,
    emptyContent,
    updateRender
  };
};

export { FbcToggleList as default, inputRegex, renderFbcToggleList };
//# sourceMappingURL=FbcToggleList.js.map
